import * as React from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type {
  OverlayDirectionStats,
  PolicyListFilter,
} from "../../../../../utils/cohortTradesResolve";

const BASE_FILTER_OPTIONS: Array<{ value: PolicyListFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "kept", label: "Kept" },
  { value: "removed", label: "Removed" },
  { value: "removed_winners", label: "Removed winners" },
  { value: "removed_losers", label: "Removed losers" },
];

const CHANGED_OPTION: { value: PolicyListFilter; label: string } = {
  value: "changed",
  label: "Changed",
};

function isPolicyListFilter(
  value: string,
  options: Array<{ value: PolicyListFilter; label: string }>
): value is PolicyListFilter {
  return options.some((opt) => opt.value === value);
}

function formatMoney(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatPct(value: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(1)}%`;
}

function policyFilterScopeLabel(filter: PolicyListFilter): string {
  switch (filter) {
    case "kept":
      return "Kept";
    case "removed":
      return "Affected (removed)";
    case "removed_winners":
      return "Removed winners";
    case "removed_losers":
      return "Removed losers";
    case "changed":
      return "Changed";
    case "all":
    default:
      return "Affected (removed)";
  }
}

function OverlayMetricsLine(props: {
  scopeLabel: string;
  stats: OverlayDirectionStats;
  scopeTotal: number | null;
}): React.ReactElement {
  const { scopeLabel, stats, scopeTotal } = props;
  return (
    <div
      data-policy-overlay-metrics=""
      className="mb-3 flex flex-wrap items-baseline gap-x-3.5 gap-y-1 font-[family-name:var(--font-primary)] text-sm font-normal leading-snug text-foreground"
    >
      {/* Overall / All */}
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="text-muted-foreground dark:text-sidebar-muted-foreground">{scopeLabel}:</span>
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">{stats.count}</span>
        {scopeTotal != null && scopeTotal > 0 ? (
          <span className="text-muted-foreground dark:text-sidebar-muted-foreground">
            of {scopeTotal} tagged
          </span>
        ) : null}
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span className="text-[color:var(--tessera-pnl-positive)]">{stats.wins}W</span>
        <span className="text-[color:var(--brand-glacial-red)]">{stats.losses}L</span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
        <span>{formatPct(stats.winRate)}</span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span
          className={cn(
            stats.netPnl >= 0
              ? "text-[color:var(--tessera-pnl-positive)]"
              : "text-[color:var(--brand-glacial-red)]"
          )}
        >
          PnL {formatMoney(stats.netPnl)}
        </span>
      </span>

      <span className="text-muted-foreground/35 dark:text-sidebar-muted-foreground/35" aria-hidden>
        |
      </span>

      {/* Longs */}
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">Longs:</span>
        <span>{stats.longs.count}</span>
        {stats.longs.count > 0 ? (
          <>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="text-[color:var(--tessera-pnl-positive)]">{stats.longs.wins}W</span>
            <span className="text-[color:var(--brand-glacial-red)]">{stats.longs.losses}L</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
            <span>{formatPct(stats.longs.winRate)}</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span
              className={cn(
                stats.longs.netPnl >= 0
                  ? "text-[color:var(--tessera-pnl-positive)]"
                  : "text-[color:var(--brand-glacial-red)]"
              )}
            >
              PnL {formatMoney(stats.longs.netPnl)}
            </span>
          </>
        ) : null}
      </span>

      <span className="text-muted-foreground/35 dark:text-sidebar-muted-foreground/35" aria-hidden>
        |
      </span>

      {/* Shorts */}
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">Shorts:</span>
        <span>{stats.shorts.count}</span>
        {stats.shorts.count > 0 ? (
          <>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="text-[color:var(--tessera-pnl-positive)]">{stats.shorts.wins}W</span>
            <span className="text-[color:var(--brand-glacial-red)]">{stats.shorts.losses}L</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
            <span>{formatPct(stats.shorts.winRate)}</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span
              className={cn(
                stats.shorts.netPnl >= 0
                  ? "text-[color:var(--tessera-pnl-positive)]"
                  : "text-[color:var(--brand-glacial-red)]"
              )}
            >
              PnL {formatMoney(stats.shorts.netPnl)}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

export type PolicyOverlaySummaryProps = {
  listFilter: PolicyListFilter;
  onListFilterChange: (filter: PolicyListFilter) => void;
  /** When true, include Exit Placement “Changed” alongside kept/removed. */
  includeChanged?: boolean;
  /** Metrics for the active filter (or Affected/removed when filter is All). */
  directionStats?: OverlayDirectionStats | null;
  /** Denominator for “N of M” (e.g. total tagged trades). */
  scopeTotal?: number | null;
};

/** Kept / removed (+ optional Changed) filter tabs for Trade Review simulation overlay. */
export function PolicyOverlaySummary(props: PolicyOverlaySummaryProps): React.ReactElement {
  const filterOptions = React.useMemo(
    () =>
      props.includeChanged ? [...BASE_FILTER_OPTIONS, CHANGED_OPTION] : BASE_FILTER_OPTIONS,
    [props.includeChanged]
  );

  const stats = props.directionStats;
  const scopeLabel = policyFilterScopeLabel(props.listFilter);
  const scopeTotal =
    typeof props.scopeTotal === "number" && Number.isFinite(props.scopeTotal)
      ? props.scopeTotal
      : null;

  return (
    <div data-policy-overlay-summary="" className="mb-4 w-full min-w-0">
      {stats && stats.count > 0 ? (
        <OverlayMetricsLine scopeLabel={scopeLabel} stats={stats} scopeTotal={scopeTotal} />
      ) : stats && stats.count === 0 ? (
        <p className="mb-3 font-[family-name:var(--font-primary)] text-sm text-muted-foreground dark:text-sidebar-muted-foreground">
          No trades in this overlay filter.
        </p>
      ) : null}

      <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden">
        <Tabs
          data-trade-log-tabs=""
          data-policy-overlay-filter-tabs=""
          value={props.listFilter}
          onValueChange={(next) => {
            if (isPolicyListFilter(next, filterOptions)) props.onListFilterChange(next);
          }}
          className="w-fit"
        >
          <TabsList className="min-w-max">
            {filterOptions.map((opt) => (
              <TabsTrigger
                key={opt.value}
                value={opt.value}
                className="font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal"
              >
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
